/**
 * Nova live run panel (native): pushed as a modal the moment "Run now" starts
 * a run (and by run deep-links from notifications). Native rendering of the web
 * {@link ./NovaRunPanel.web}: live step checklist with a spinner on the
 * executing step, real results (job matches / digest) as they land, and inline
 * Approve/Reject when the run pauses for a draft. Logic lives in
 * {@link ./nova-run-watch}; this file renders only.
 */
import { useState } from 'react';
import { useLocalSearchParams, useRouter } from 'expo-router';
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { Text } from '@/components/common/AppText';
import { useTranslation } from 'react-i18next';
import {
  AlertCircle,
  CheckCircle2,
  ChevronRight,
  Circle,
  Clock,
  MinusCircle,
} from 'lucide-react-native';
import { colors, spacing } from '@tabbio/tokens';
import type { NovaRunMatchItem, NovaRunResultPresentation } from '@tabbio/types';
import { useTheme } from '../../hooks/useTheme';
import { useFont } from '../../providers/FontProvider';
import { useLocale } from '../../hooks/useLocale';
import { useHaptics } from '../../hooks/useHaptics';
import { useToast } from '../../hooks/useToast';
import { approveRun, rejectRun } from '../../lib/automation-api';
import { NativeButton } from '../common/NativeButton';
import {
  extractRunDigest,
  extractRunMatches,
  mergeRunSteps,
  stepLabel,
  stepResultSummary,
  useNovaRunWatch,
  runFailureHint,
} from './nova-run-watch';
import {
  extractRunPresentation,
  isQuietReportRun,
} from './nova-run-presentation';
import { extractRunArtifacts } from './nova-run-artifacts';
import { NovaMascot } from './nova-ui-native';
import { ArtifactPreviewCard } from '../assistant-ui/tool-cards/artifact-card';

const AMBER_LIGHT = '#b26a00';
const AMBER_DARK = '#F59E0B';

export function NovaRunPanelScreen() {
  const params = useLocalSearchParams<{ runId?: string; name?: string }>();
  const runId = typeof params.runId === 'string' ? params.runId : '';
  const name = typeof params.name === 'string' ? params.name : '';
  const { t } = useTranslation('common');
  const { theme, isDark } = useTheme();
  const { fonts } = useFont();
  const { isRTL } = useLocale();
  const haptics = useHaptics();
  const toast = useToast();
  const router = useRouter();
  const rowDirection = isRTL ? 'row-reverse' : 'row';
  const amber = isDark ? AMBER_DARK : AMBER_LIGHT;

  const { detail, error, notFound, live, refresh } = useNovaRunWatch(
    runId || null,
  );
  const [resolving, setResolving] = useState(false);

  const status = detail?.run.status ?? 'queued';
  const checklist = mergeRunSteps(detail);
  const matches = extractRunMatches(detail);
  const digest = extractRunDigest(detail);
  const presentation = extractRunPresentation(detail);
  const quietReport = isQuietReportRun(detail);
  const artifacts = extractRunArtifacts(detail);
  const awaiting = status === 'awaiting_approval';
  const failed = status === 'failed' || status === 'killed';
  const succeeded = status === 'succeeded';

  const resolve = async (approve: boolean) => {
    if (resolving) return;
    haptics.trigger('light');
    setResolving(true);
    try {
      if (approve) await approveRun(runId);
      else await rejectRun(runId);
      toast.success(approve ? t('nova.runs.approved') : t('nova.runs.rejected'));
      refresh();
    } catch {
      toast.error(t('nova.runs.approveFailed'));
    } finally {
      setResolving(false);
    }
  };

  const openJob = (match: NovaRunMatchItem) => {
    haptics.trigger('light');
    router.push(
      `/(app)/jobs/${encodeURIComponent(match.catalogJobId)}` as never,
    );
  };

  // NativeButton already plays the press haptic for both state actions.
  const backToTasks = () => {
    if (router.canGoBack()) {
      router.back();
      return;
    }
    router.replace('/auto-apply' as never);
  };

  // A missing run and a failed load are states, not spinners.
  const loadFailed = !notFound && error && !detail;
  const showSpinner = (live || !detail) && !notFound && !loadFailed;

  let heroTitle: string;
  let heroHint: string | null = null;
  let heroColor = theme.text;
  if (notFound) {
    heroTitle = t('nova.runPanel.notFoundTitle');
    heroHint = t('nova.runPanel.notFoundHint');
  } else if (loadFailed) {
    heroTitle = t('nova.runPanel.loadFailed');
  } else if (live || !detail) {
    heroTitle = t('nova.runPanel.liveTitle');
    heroHint = t('nova.runPanel.liveHint');
  } else if (awaiting) {
    heroTitle = t('nova.runPanel.awaitingTitle');
    heroHint = t('nova.runPanel.awaitingHint');
    heroColor = amber;
  } else if (failed) {
    heroTitle = t('nova.runPanel.failedTitle');
    heroHint = runFailureHint(t, detail?.run.errorSummary);
    heroColor = theme.danger;
  } else if (succeeded && matches.length > 0) {
    heroTitle = t('nova.runPanel.doneWithMatches', { count: matches.length });
    heroHint = t('nova.runPanel.doneHint');
    heroColor = theme.success;
  } else if (succeeded && quietReport) {
    // A research re-run with nothing new: delivered quietly.
    heroTitle = t('nova.results.nothingNew');
    heroHint = t('nova.runPanel.nothingNewHint');
  } else if (succeeded && presentation) {
    heroTitle = presentation.title || t('nova.runPanel.doneGeneric');
    heroHint = t('nova.runPanel.doneGenericHint');
    heroColor = theme.success;
  } else if (succeeded && artifacts.length > 0) {
    heroTitle = t('nova.runPanel.doneGeneric');
    heroHint = t('nova.runPanel.doneGenericHint');
    heroColor = theme.success;
  } else {
    heroTitle = t('nova.runPanel.noResults');
    heroHint = t('nova.runPanel.noResultsHint');
  }

  return (
    <ScrollView
      style={[styles.screen, { backgroundColor: theme.background }]}
      contentContainerStyle={styles.content}
      contentInsetAdjustmentBehavior="automatic"
    >
      {name ? (
        <Text
          style={[styles.name, { color: theme.textSecondary, fontFamily: fonts.regular, writingDirection: 'auto' }]}
          numberOfLines={1}
        >
          {name}
        </Text>
      ) : null}

      {/* Status hero */}
      <View style={[styles.hero, { flexDirection: rowDirection }]}>
        <View style={[styles.heroAvatar, { backgroundColor: theme.surfaceElevated, borderColor: theme.border }]}>
          {showSpinner ? (
            <ActivityIndicator size="small" color={colors.primary} />
          ) : (
            <NovaMascot size={30} />
          )}
        </View>
        <View style={styles.heroText}>
          <Text style={[styles.heroTitle, { color: heroColor, fontFamily: fonts.semibold, writingDirection: 'auto' }]}>
            {heroTitle}
          </Text>
          {heroHint ? (
            <Text style={[styles.heroHint, { color: theme.textSecondary, fontFamily: fonts.regular, writingDirection: 'auto' }]}>
              {heroHint}
            </Text>
          ) : null}
        </View>
      </View>

      {/* A deleted / foreign run: one way out, no retry loop. */}
      {notFound ? (
        <NativeButton
          title={t('nova.runPanel.backToTasks')}
          color="contrast"
          pill
          onPress={backToTasks}
          block
        />
      ) : null}

      {/* Transient or exhausted load failure: the user decides to retry. */}
      {loadFailed ? (
        <NativeButton
          title={t('nova.runPanel.retry')}
          color="contrast"
          pill
          onPress={refresh}
          block
        />
      ) : null}

      {/* Inline approval */}
      {awaiting ? (
        <View style={[styles.approvalBanner, { flexDirection: rowDirection }]}>
          <Text style={[styles.approvalText, { color: theme.text, fontFamily: fonts.regular }]}>
            {t('nova.approvals.draftReady')}
          </Text>
          <View style={[styles.approvalActions, { flexDirection: rowDirection }]}>
            <NativeButton
              title={t('nova.runs.reject')}
              variant="outline"
              pill
              onPress={() => void resolve(false)}
              disabled={resolving}
              style={styles.approvalBtn}
            />
            <NativeButton
              title={t('nova.runs.approve')}
              color="contrast"
              pill
              onPress={() => void resolve(true)}
              disabled={resolving}
              style={styles.approvalBtn}
            />
          </View>
        </View>
      ) : null}

      {/* Live step checklist */}
      {checklist.length > 0 ? (
        <View style={styles.section}>
          <Text style={[styles.sectionLabel, { color: theme.textTertiary, fontFamily: fonts.medium }]}>
            {t('nova.runs.stepsLabel')}
          </Text>
          {checklist.map((item) => {
            const summary = stepResultSummary(t, item);
            return (
              <View key={item.stepId} style={[styles.stepRow, { flexDirection: rowDirection }]}>
                <View style={styles.stepIcon}>
                  {item.status === 'running' ? (
                    <ActivityIndicator size="small" color={colors.primary} />
                  ) : item.status === 'succeeded' ? (
                    <CheckCircle2 size={16} color={theme.success} strokeWidth={2} />
                  ) : item.status === 'failed' ? (
                    <AlertCircle size={16} color={theme.danger} strokeWidth={2} />
                  ) : item.status === 'awaiting_approval' ? (
                    <Clock size={16} color={amber} strokeWidth={2} />
                  ) : item.status === 'skipped' ? (
                    <MinusCircle size={16} color={theme.textTertiary} strokeWidth={2} />
                  ) : (
                    <Circle size={16} color={theme.textTertiary} strokeWidth={1.8} />
                  )}
                </View>
                <View style={styles.stepText}>
                  <View style={[styles.stepTitleRow, { flexDirection: rowDirection }]}>
                    <Text
                      style={[
                        styles.stepTitle,
                        {
                          color: item.status === 'pending' ? theme.textTertiary : theme.text,
                          fontFamily: fonts.regular,
                          writingDirection: 'auto',
                        },
                      ]}
                    >
                      {stepLabel(t, item.type)}
                    </Text>
                    {item.dryRun ? (
                      <View style={[styles.draftBadge, { backgroundColor: theme.surfaceSecondary }]}>
                        <Text style={[styles.draftBadgeText, { color: theme.textSecondary, fontFamily: fonts.medium }]}>
                          {t('nova.runs.draftLabel')}
                        </Text>
                      </View>
                    ) : null}
                  </View>
                  {summary ? (
                    <Text style={[styles.stepSummary, { color: theme.textTertiary, fontFamily: fonts.regular, writingDirection: 'auto' }]}>
                      {summary}
                    </Text>
                  ) : null}
                </View>
              </View>
            );
          })}
        </View>
      ) : null}

      {/* Results: the actual jobs */}
      {matches.length > 0 ? (
        <View style={styles.section}>
          <Text style={[styles.sectionLabel, { color: theme.textTertiary, fontFamily: fonts.medium }]}>
            {digest?.title || t('nova.runPanel.resultsLabel')}
          </Text>
          <View style={[styles.resultList, { borderColor: theme.border }]}>
            {matches.map((match, i) => (
              <NovaMatchRowNative
                key={match.catalogJobId}
                match={match}
                last={i === matches.length - 1}
                rowDirection={rowDirection}
                onOpen={() => openJob(match)}
              />
            ))}
          </View>
        </View>
      ) : null}

      {presentation ? (
        <NovaGenericResultNative presentation={presentation} rowDirection={rowDirection} />
      ) : null}

      {/* Files the run produced: the same card as in chat. */}
      {artifacts.length > 0 ? (
        <View style={styles.section}>
          <Text style={[styles.sectionLabel, { color: theme.textTertiary, fontFamily: fonts.medium }]}>
            {t('chat.artifacts.runLabel')}
          </Text>
          {artifacts.map((artifact) => (
            <ArtifactPreviewCard key={artifact.artifactId} artifact={artifact} />
          ))}
        </View>
      ) : null}
    </ScrollView>
  );
}

function NovaGenericResultNative({
  presentation,
  rowDirection,
}: {
  presentation: NovaRunResultPresentation;
  rowDirection: 'row' | 'row-reverse';
}) {
  const { t } = useTranslation('common');
  const { theme } = useTheme();
  const { fonts } = useFont();
  return (
    <View style={styles.section}>
      <Text style={[styles.sectionLabel, { color: theme.textTertiary, fontFamily: fonts.medium }]}>
        {t('nova.runPanel.detailsLabel')}
      </Text>
      {presentation.summary ? (
        <Text style={[styles.genericSummary, { color: theme.textSecondary, fontFamily: fonts.regular, writingDirection: 'auto' }]}>
          {presentation.summary}
        </Text>
      ) : null}
      {presentation.stats?.length ? (
        <View style={[styles.statGrid, { flexDirection: rowDirection }]}>
          {presentation.stats.map((stat, index) => (
            <View key={`${stat.id}-${index}`} style={[styles.statCard, { backgroundColor: theme.surface, borderColor: theme.border }]}>
              <Text style={[styles.statValue, { color: theme.text, fontFamily: fonts.semibold }]} numberOfLines={1}>
                {String(stat.value)}
              </Text>
              <Text style={[styles.statLabel, { color: theme.textTertiary, fontFamily: fonts.regular, writingDirection: 'auto' }]} numberOfLines={2}>
                {stat.label}
              </Text>
            </View>
          ))}
        </View>
      ) : null}
      {presentation.sections?.map((section, sectionIndex) => (
        <View key={`${section.id}-${sectionIndex}`} style={[styles.genericSection, { backgroundColor: theme.surface, borderColor: theme.border }]}>
          {section.title ? (
            <Text style={[styles.genericSectionTitle, { color: theme.text, fontFamily: fonts.semibold, writingDirection: 'auto' }]}>
              {section.title}
            </Text>
          ) : null}
          {section.body ? (
            <Text style={[styles.genericSectionBody, { color: theme.textSecondary, fontFamily: fonts.regular, writingDirection: 'auto' }]}>
              {section.body}
            </Text>
          ) : null}
          {section.items?.map((item, itemIndex) => (
            <View key={`${item.id}-${itemIndex}`} style={[styles.genericItem, { borderTopColor: theme.border, flexDirection: rowDirection }]}>
              <View style={styles.genericItemText}>
                <Text style={[styles.genericItemTitle, { color: theme.text, fontFamily: fonts.medium, writingDirection: 'auto' }]}>
                  {item.title}
                </Text>
                {item.subtitle || item.body ? (
                  <Text style={[styles.genericItemBody, { color: theme.textTertiary, fontFamily: fonts.regular, writingDirection: 'auto' }]}>
                    {[item.subtitle, item.body].filter(Boolean).join('\n')}
                  </Text>
                ) : null}
              </View>
              {item.badge ? (
                <View style={[styles.genericBadge, { backgroundColor: theme.surfaceSecondary }]}>
                  <Text style={[styles.genericBadgeText, { color: theme.textSecondary, fontFamily: fonts.medium }]}>
                    {item.badge}
                  </Text>
                </View>
              ) : null}
            </View>
          ))}
        </View>
      ))}
    </View>
  );
}

/** Compact, pressable job-match row: shared with the run-history screen. */
export function NovaMatchRowNative({
  match,
  last,
  rowDirection,
  onOpen,
}: {
  match: NovaRunMatchItem;
  last: boolean;
  rowDirection: 'row' | 'row-reverse';
  onOpen: () => void;
}) {
  const { t } = useTranslation('common');
  const { theme } = useTheme();
  const { fonts } = useFont();
  const score =
    typeof match.matchScore === 'number'
      ? Math.round(match.matchScore <= 1 ? match.matchScore * 100 : match.matchScore)
      : null;
  const companyLine = [match.companyName, match.location].filter(Boolean).join(' · ');
  return (
    <Pressable
      onPress={onOpen}
      style={({ pressed }) => [
        styles.matchRow,
        {
          flexDirection: rowDirection,
          borderBottomColor: theme.border,
          borderBottomWidth: last ? 0 : StyleSheet.hairlineWidth,
          opacity: pressed ? 0.7 : 1,
        },
      ]}
    >
      <View style={styles.matchText}>
        <Text
          style={[styles.matchTitle, { color: theme.text, fontFamily: fonts.semibold, writingDirection: 'auto' }]}
          numberOfLines={1}
        >
          {match.jobTitle}
        </Text>
        {companyLine ? (
          <Text
            style={[styles.matchCompany, { color: theme.textTertiary, fontFamily: fonts.regular, writingDirection: 'auto' }]}
            numberOfLines={1}
          >
            {companyLine}
          </Text>
        ) : null}
      </View>
      {score != null ? (
        <View style={styles.scoreBadge}>
          <Text style={[styles.scoreText, { fontFamily: fonts.semibold }]}>
            {t('nova.results.scoreLabel', { score })}
          </Text>
        </View>
      ) : null}
      <ChevronRight
        size={18}
        color={theme.textTertiary}
        strokeWidth={2}
        style={{ transform: [{ scaleX: rowDirection === 'row-reverse' ? -1 : 1 }] }}
      />
    </Pressable>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1 },
  content: { paddingHorizontal: spacing[4], paddingTop: spacing[3], paddingBottom: 48, gap: spacing[5] },
  name: { fontSize: 13 },
  hero: { alignItems: 'center', gap: 14 },
  heroAvatar: {
    width: 48,
    height: 48,
    borderRadius: 14,
    borderWidth: StyleSheet.hairlineWidth,
    alignItems: 'center',
    justifyContent: 'center',
  },
  heroText: { flex: 1, minWidth: 0 },
  heroTitle: { fontSize: 16, fontWeight: '600' },
  heroHint: { fontSize: 13, lineHeight: 18, marginTop: 2 },
  approvalBanner: {
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(178,106,0,0.28)',
    backgroundColor: 'rgba(178,106,0,0.06)',
    padding: 14,
    gap: 10,
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  approvalText: { flex: 1, minWidth: 160, fontSize: 13.5, lineHeight: 19 },
  approvalActions: { gap: 8, alignItems: 'center' },
  approvalBtn: { minWidth: 96 },
  section: { gap: 10 },
  sectionLabel: { fontSize: 11, fontWeight: '600', letterSpacing: 0.4, textTransform: 'uppercase' },
  stepRow: { alignItems: 'flex-start', gap: 10 },
  stepIcon: { width: 20, height: 20, alignItems: 'center', justifyContent: 'center', marginTop: 1 },
  stepText: { flex: 1, minWidth: 0 },
  stepTitleRow: { alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  stepTitle: { fontSize: 13.5, lineHeight: 19 },
  stepSummary: { fontSize: 12.5, lineHeight: 17, marginTop: 1 },
  draftBadge: { borderRadius: 9999, paddingHorizontal: 8, paddingVertical: 2 },
  draftBadgeText: { fontSize: 11, fontWeight: '500' },
  resultList: { borderRadius: 14, borderWidth: StyleSheet.hairlineWidth, overflow: 'hidden' },
  matchRow: { alignItems: 'center', gap: 10, paddingVertical: 11, paddingHorizontal: 14 },
  matchText: { flex: 1, minWidth: 0 },
  matchTitle: { fontSize: 14, fontWeight: '600' },
  matchCompany: { fontSize: 12.5, marginTop: 1 },
  scoreBadge: {
    borderRadius: 9999,
    paddingHorizontal: 9,
    paddingVertical: 3,
    backgroundColor: 'rgba(94,41,255,0.08)',
  },
  scoreText: { fontSize: 12, fontWeight: '600', color: colors.primary },
  genericSummary: { fontSize: 14, lineHeight: 21 },
  statGrid: { flexWrap: 'wrap', gap: 10 },
  statCard: { minWidth: 132, flexGrow: 1, flexBasis: '45%', borderRadius: 14, borderWidth: StyleSheet.hairlineWidth, padding: 14, gap: 3 },
  statValue: { fontSize: 22, lineHeight: 27, fontWeight: '600' },
  statLabel: { fontSize: 12.5, lineHeight: 17 },
  genericSection: { borderRadius: 14, borderWidth: StyleSheet.hairlineWidth, padding: 14, gap: 8 },
  genericSectionTitle: { fontSize: 15, lineHeight: 20, fontWeight: '600' },
  genericSectionBody: { fontSize: 13.5, lineHeight: 20 },
  genericItem: { alignItems: 'flex-start', gap: 10, borderTopWidth: StyleSheet.hairlineWidth, paddingTop: 10 },
  genericItemText: { flex: 1, minWidth: 0, gap: 2 },
  genericItemTitle: { fontSize: 13.5, lineHeight: 19, fontWeight: '500' },
  genericItemBody: { fontSize: 12.5, lineHeight: 18 },
  genericBadge: { borderRadius: 9999, paddingHorizontal: 8, paddingVertical: 3 },
  genericBadgeText: { fontSize: 11, fontWeight: '500' },
});
